import React from 'react'
import './DuplicateCourtModal.css'

/**
 * DuplicateCourtModal - Shows when user tries to add a court that may already exist
 *
 * Props:
 * - isOpen: boolean - controls modal visibility
 * - onClose: function - called when user closes modal
 * - onSelectExisting: function - called when user selects an existing court
 * - onCreateAnyway: function - called when user wants to create despite duplicate
 * - duplicates: array - array of potential duplicate courts
 * - isProcessing: boolean - shows loading state during API call
 */
export default function DuplicateCourtModal({
  isOpen,
  onClose,
  onSelectExisting,
  onCreateAnyway,
  duplicates = [],
  isProcessing = false,
}) {
  if (!isOpen) return null

  // Helper function to format court address
  const formatCourtAddress = (court) => {
    return [court.address_line1, court.city, court.state, court.zip]
      .filter(Boolean)
      .join(', ')
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose()
    }
  }

  const handleSelectCourt = (court) => {
    if (!isProcessing) {
      onSelectExisting(court)
    }
  }

  const handleCreateAnyway = () => {
    if (!isProcessing) {
      onCreateAnyway()
    }
  }

  const handleClose = () => {
    if (!isProcessing) {
      onClose()
    }
  }

  return (
    <div
      className="duplicate-court-modal-overlay"
      onClick={handleBackdropClick}
    >
      <div
        className="duplicate-court-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="duplicate-court-modal-title"
      >
        <div className="modal-header">
          <h2 id="duplicate-court-modal-title" className="modal-title">
            Court May Already Exist
          </h2>
          {!isProcessing && (
            <button
              className="modal-close"
              onClick={handleClose}
              aria-label="Close modal"
            >
              ✕
            </button>
          )}
        </div>

        <div className="modal-body">
          <div className="info-icon" aria-hidden="true">
            🎾
          </div>

          <p className="info-message">Did you mean one of these courts?</p>

          <div className="duplicates-list">
            {duplicates.map((court) => (
              <button
                key={court.id}
                className="duplicate-court-item"
                onClick={() => handleSelectCourt(court)}
                disabled={isProcessing}
                aria-label={`Select ${court.name} at ${formatCourtAddress(court)}`}
              >
                <div className="court-name">{court.name}</div>
                <div className="court-address">{formatCourtAddress(court)}</div>
                {court.surface && (
                  <div className="court-details">
                    Surface: {court.surface}
                    {court.lighting && ' • Lights available'}
                  </div>
                )}
              </button>
            ))}
          </div>

          <p className="small text-muted mt-3 mb-0">
            If none of these match, please contact support to add this court.
          </p>
        </div>

        <div className="modal-footer">
          <button
            className="duplicate-court-btn cancel-btn"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            className="duplicate-court-btn create-anyway-btn"
            onClick={handleCreateAnyway}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              'None of these match'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
